import { useEffect, useState, useRef } from "react";
import { Bell, Check, MessageCircle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatXOF } from "@/lib/api";

const POLL_MS = 12000;

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const lastUnreadRef = useRef(0);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const fetchNotifs = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
      // beep if a new unread appeared
      if (data.unread > lastUnreadRef.current && lastUnreadRef.current > 0) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.frequency.value = 880; g.gain.value = 0.05;
          o.start(); o.stop(ctx.currentTime + 0.18);
        } catch (e) { /* ignore */ }
      }
      lastUnreadRef.current = data.unread || 0;
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [isAdmin]);

  if (!isAdmin) return null;

  const markRead = async (n) => {
    await api.put(`/admin/notifications/${n.id}/read`);
    fetchNotifs();
  };

  const markAll = async () => {
    await api.put("/admin/notifications/read-all");
    fetchNotifs();
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button onClick={() => setOpen((v) => !v)} className="relative grid place-items-center w-10 h-10 rounded-full hover:bg-sage-50 transition" aria-label="Notifications" data-testid="bell-button">
        <Bell className="w-5 h-5 text-ink" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold-dark text-white text-[10px] font-semibold grid place-items-center" data-testid="bell-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-[360px] max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-border z-50" data-testid="bell-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-white">
            <div className="font-display text-lg">Notifications</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-sage-600 hover:underline" data-testid="bell-mark-all">Tout marquer lu</button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-beige"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-ink-mute text-sm">Aucune notification.</div>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id} className={`px-4 py-3 border-b border-border/60 ${!n.read ? "bg-sage-50/30" : ""}`} data-testid={`notif-${n.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wider text-gold-dark font-semibold">Paiement Wave déclaré</div>
                      <div className="text-sm text-ink mt-1 truncate">
                        <strong>{n.user_name || "Anonyme"}</strong> · {formatXOF(n.amount)}
                      </div>
                      <div className="text-xs text-ink-mute mt-0.5 font-mono">{n.reference || "—"}</div>
                      <div className="text-[11px] text-ink-mute mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
                      {n.sent_auto && <div className="mt-1 text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100"><Check className="w-3 h-3" /> WhatsApp auto envoyé</div>}
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {n.whatsapp_link && (
                        <a href={n.whatsapp_link} target="_blank" rel="noreferrer" className="btn-pill bg-[#25D366] text-white px-3 py-1.5 text-[11px] hover:bg-[#1bb056]" data-testid={`notif-wa-${n.id}`}>
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                      {!n.read && (
                        <button onClick={() => markRead(n)} className="text-[10px] text-ink-mute hover:text-sage-600" data-testid={`notif-read-${n.id}`}>marquer lu</button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
